import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { SafeArea, SystemBarsStyle, SystemBarsType } from '@capacitor-community/safe-area';
import { VoiceRecorder as CapacitorVoiceRecorder } from 'capacitor-voice-recorder';
import { PushNotifications as CapacitorPushNotifications } from '@capacitor/push-notifications';
import { PushNotificationManager } from './push-notifications';

/**
 * =============================================================================
 * THEME & UTILS
 * =============================================================================
 */

const CapacitorUtils = {
    _cache: { isNative: null, platform: null },

    isNative() {
        if (this._cache.isNative === null) this._cache.isNative = Capacitor.isNativePlatform();
        return this._cache.isNative;
    },

    getPlatform() {
        if (this._cache.platform === null) this._cache.platform = Capacitor.getPlatform();
        return this._cache.platform;
    },

    isAndroid() {
        return this.getPlatform() === 'android';
    },
    isIOS() {
        return this.getPlatform() === 'ios';
    },
    isWeb() {
        return this.getPlatform() === 'web';
    },

    clearCache() {
        this._cache.isNative = null;
        this._cache.platform = null;
    },
};

// the width below which the sign-in screens stack the brand block over the form (theme.css)
const AUTH_PHONE_QUERY = '(max-width: 1023px)';

const ThemeUtils = {
    isDarkMode() {
        return (
            document.documentElement.classList.contains('dark') ||
            localStorage.getItem('theme') === 'dark' ||
            (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches)
        );
    },

    // the sign-in screens run the purple brand block under the status bar on a
    // phone (theme.css, "Below the split"), so the status bar needs light icons in
    // light mode too. Only the status bar: the bottom of those screens is the sheet.
    hasDarkStatusBarGround() {
        return (
            this.isDarkMode() ||
            (document.querySelector('.nx-auth-brand') !== null && window.matchMedia(AUTH_PHONE_QUERY).matches)
        );
    },
};

const isSafeHttpUrl = value => {
    try {
        const u = new URL(value, window.location.origin);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
};

// Debounce utility
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * =============================================================================
 * SAFE AREA
 * =============================================================================
 */

const SafeAreaManager = {
    _currentStyle: null,

    /**
     * Latched the first time the installed build turns out not to carry
     * setSystemBarsStyle. Named for the one method rather than for the plugin:
     * showSystemBars and hideSystemBars are separate bridge methods, and a
     * build missing one may well carry the others.
     */
    _systemBarsStyleUnsupported: false,

    /**
     * v8 of the plugin exposes only setSystemBarsStyle, showSystemBars and
     * hideSystemBars. enable() and getSafeAreaInsets() — the v7-alpha API this
     * file used to call — exist on neither platform, so the call threw
     * "SafeArea.enable() is not implemented on android" at every boot and again
     * on every theme change (Sentry N-AI-QQ-116).
     *
     * Insets now need no JavaScript at all. The plugin is a native polyfill: on
     * webviews older than Chromium 140 it pads the webview itself and reports
     * env(safe-area-inset-*) as 0px; on newer ones it passes the real values
     * through. Either way env() is correct, and app.css already reads it.
     *
     * The machinery that used to live here read those custom properties back and
     * rewrote them as inline pixels. getPropertyValue returns the unresolved
     * `env(...)` token stream rather than a length, so it parsed NaN, fell to 0,
     * and pinned every inset to 0px — on load, on livewire:navigated, on
     * turbo:load. That is what put the bottom nav's labels under the gesture bar,
     * and what the "CSS variables were reset!" watchdog was chasing.
     */
    async initialize() {
        if (!CapacitorUtils.isNative()) return;

        await this.applySystemBarsStyle(ThemeUtils.isDarkMode(), ThemeUtils.hasDarkStatusBarGround());
    },

    /**
     * SystemBarsStyle names the BACKGROUND the bars sit on, not their content:
     * Dark means light icons on a dark background. Read the other way round it
     * renders the status bar icons invisible against the app behind them.
     *
     * v8 no longer sets the bars' background colours at all — they are
     * transparent under edge-to-edge and the page shows through, which is what
     * .notch-background and .nx-bottom-nav paint.
     */
    async applySystemBarsStyle(isDark, isStatusBarDark = isDark) {
        if (!CapacitorUtils.isNative() || this._systemBarsStyleUnsupported) return;

        const style = isDark ? SystemBarsStyle.Dark : SystemBarsStyle.Light;
        const statusBarStyle = isStatusBarDark ? SystemBarsStyle.Dark : SystemBarsStyle.Light;
        const combined = `${style}|${statusBarStyle}`;

        if (combined === this._currentStyle) return;

        try {
            // both bars first, then the status bar alone when its ground differs
            await SafeArea.setSystemBarsStyle({ style });

            if (statusBarStyle !== style) {
                await SafeArea.setSystemBarsStyle({ style: statusBarStyle, type: SystemBarsType.StatusBar });
            }

            this._currentStyle = combined;
        } catch (error) {
            // an installed build whose native plugin predates the v8 API cannot
            // grow the method later in the session, and _currentStyle only
            // latches on success — so before this, the boot call and every
            // theme change after it retried and re-reported forever. That is
            // Sentry N-AI-QQ-118 and -119: one catch, split into two issues by
            // which path called it (initialize() vs syncWithFilamentTheme).
            //
            // Still reported once. This file has now shipped a call to a method
            // the native side does not have TWICE — enable() before this
            // (N-AI-QQ-116), setSystemBarsStyle now — and going silent in code
            // would hide the third, including on a fresh build where it would
            // be a real bug rather than a legacy install. One event per boot is
            // the canary; Sentry's archive-until-escalating handles the volume.
            if (this.isUnimplemented(error)) {
                this._systemBarsStyleUnsupported = true;
            }

            console.error('[SafeArea] Failed to set system bars style:', error);
            if (typeof Sentry !== 'undefined' && Sentry.captureException) {
                Sentry.captureException(error, {
                    tags: { component: 'safe_area', context: 'system_bars_style' },
                    level: 'warning',
                });
            }
        }
    },

    /**
     * Whether the bridge rejected the call because the method is not there.
     *
     * Capacitor throws this in JavaScript before it ever reaches the native
     * side, when the registered plugin does not carry the method, and tags it
     * ExceptionCode.Unimplemented — which is the discriminator, not the text.
     *
     * The message test is only a fallback for an error that lost `code`
     * crossing a realm or being reserialized, and it is scoped to THIS plugin's
     * two templates — the method missing ("SafeArea.setSystemBarsStyle()" is
     * not implemented on …) and the whole plugin missing ("SafeArea" plugin is
     * not implemented on …), both of which mean this call can never work on
     * this build. Left unscoped it would latch on any other plugin's
     * unimplemented error and switch off system bar styling for the whole boot
     * over an unrelated failure.
     */
    isUnimplemented(error) {
        if (error?.code === 'UNIMPLEMENTED') return true;

        const message = typeof error?.message === 'string' ? error.message : '';

        return message.includes('is not implemented on') && message.includes('SafeArea');
    },

    // Debounced theme sync to prevent rapid calls
    syncWithFilamentTheme: debounce(async function () {
        await SafeAreaManager.applySystemBarsStyle(ThemeUtils.isDarkMode(), ThemeUtils.hasDarkStatusBarGround());
    }, 100),

    watchFilamentTheme() {
        if (!CapacitorUtils.isNative()) return;

        const themeObserver = new MutationObserver(mutations => {
            for (const m of mutations) {
                if (m.attributeName === 'class') this.syncWithFilamentTheme();
            }
        });

        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        window.addEventListener('storage', e => {
            if (e.key === 'theme') this.syncWithFilamentTheme();
        });

        window.addEventListener('theme-changed', () => {
            this.syncWithFilamentTheme();
        });

        // signing in swaps the page without a reload (the panels are SPA), and the
        // brand block that forced light icons is gone from the page that replaces it
        document.addEventListener('livewire:navigated', () => {
            this.syncWithFilamentTheme();
        });

        // rotating a tablet can cross the breakpoint without any navigation, moving the
        // purple brand block in or out from under the status bar
        window.matchMedia(AUTH_PHONE_QUERY).addEventListener('change', () => {
            this.syncWithFilamentTheme();
        });

        this.syncWithFilamentTheme();
    },
};

/**
 * =============================================================================
 * CAPACITOR DETECTION (cookies + headers)
 * =============================================================================
 */

const Detection = (() => {
    const getSecureAttr = () => (window.location?.protocol === 'https:' ? '; Secure' : '');

    const setCookies = platform => {
        const secureAttr = getSecureAttr();
        document.cookie = `capacitor_app=true; path=/; max-age=31536000; SameSite=Strict${secureAttr}`;
        document.cookie = `capacitor_platform=${platform}; path=/; max-age=31536000; SameSite=Strict${secureAttr}`;
    };

    const clearCookies = () => {
        const secureAttr = getSecureAttr();
        document.cookie = `capacitor_app=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict${secureAttr}`;
        document.cookie = `capacitor_platform=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict${secureAttr}`;
    };

    const attachLivewireHook = platform => {
        if (!window.Livewire) return;
        if (window.__CapacitorLivewireHookAttached__) return;
        window.__CapacitorLivewireHookAttached__ = true;

        window.Livewire.hook('request', ({ options }) => {
            options.headers = options.headers || {};
            options.headers['X-Capacitor-App'] = 'true';
            options.headers['X-Capacitor-Platform'] = platform;
        });
    };

    const isSameOrigin = input => {
        try {
            let url = null;
            if (typeof input === 'string') url = new URL(input, window.location.href);
            else if (input instanceof URL) url = input;
            else if (input && typeof input === 'object' && 'url' in input)
                url = new URL(input.url, window.location.href);
            return url ? url.origin === window.location.origin : false;
        } catch {
            return false;
        }
    };

    const setCapacitorHeaders = (headers, platform) => {
        if (headers instanceof Headers) {
            headers.set('X-Capacitor-App', 'true');
            headers.set('X-Capacitor-Platform', platform);
            return headers;
        }
        if (Array.isArray(headers)) {
            const h = new Headers(headers);
            h.set('X-Capacitor-App', 'true');
            h.set('X-Capacitor-Platform', platform);
            return h;
        }
        if (!headers || typeof headers !== 'object') headers = {};
        headers['X-Capacitor-App'] = 'true';
        headers['X-Capacitor-Platform'] = platform;
        return headers;
    };

    const patchFetch = platform => {
        if (window.__CapacitorFetchPatched__) return;
        window.__CapacitorFetchPatched__ = true;

        const originalFetch = window.fetch;

        window.fetch = function (input, init) {
            if (!isSameOrigin(input)) {
                return originalFetch.call(this, input, init);
            }

            if (init) {
                if (init.headers) {
                    const updated = setCapacitorHeaders(init.headers, platform);
                    if (updated !== init.headers) init.headers = updated;
                } else {
                    init.headers = setCapacitorHeaders({}, platform);
                }
                return originalFetch.call(this, input, init);
            }

            if (input instanceof Request) {
                const headers = new Headers(input.headers);
                headers.set('X-Capacitor-App', 'true');
                headers.set('X-Capacitor-Platform', platform);
                const req = new Request(input, { headers });
                return originalFetch.call(this, req);
            }

            const minimalInit = { headers: setCapacitorHeaders({}, platform) };
            return originalFetch.call(this, input, minimalInit);
        };
    };

    const init = () => {
        if (CapacitorUtils.isNative()) {
            const platform = CapacitorUtils.getPlatform();
            setCookies(platform);

            if (window.Livewire) attachLivewireHook(platform);
            document.addEventListener('livewire:load', () => attachLivewireHook(platform));

            patchFetch(platform);
        } else {
            clearCookies();
        }
    };

    return { init };
})();

// Boot detection immediately
Detection.init();

/**
 * =============================================================================
 * EXTERNAL LINKS
 * =============================================================================
 */

// explicit web handoffs use the default browser, never an embedded browser session.
const ExternalLinks = (() => {
    const init = () => {
        if (!CapacitorUtils.isNative()) return;

        // delegated so it survives livewire morphs and lazy-loaded panels.
        document.addEventListener('click', async event => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            )
                return;
            const link = event.target instanceof Element ? event.target.closest('a[data-external]') : null;
            if (!link || !link.href || !isSafeHttpUrl(link.href)) return;

            if (!Capacitor.isPluginAvailable('AppLauncher') && CapacitorUtils.getPlatform() === 'ios') {
                // existing iOS binaries already send new-window links to UIApplication.open.
                link.target = '_blank';
                return;
            }

            event.preventDefault();
            try {
                if (!Capacitor.isPluginAvailable('AppLauncher')) throw new Error('AppLauncher unavailable');
                const result = await AppLauncher.openUrl({ url: link.href });
                if (!result.completed) throw new Error('External browser did not open');
            } catch (error) {
                console.error('[ExternalLinks] Could not open the external browser:', error);
                // old Android binaries cannot gain a native plugin from a web deploy.
                // keep the destination visible instead of silently opening the embedded browser.
                window.alert(link.dataset.externalError || link.href);
            }
        });
    };

    return { init };
})();

ExternalLinks.init();

// SafeArea boot - single execution path
(async () => {
    if (document.readyState === 'loading') {
        await new Promise(resolve => {
            document.addEventListener('DOMContentLoaded', resolve);
        });
    }
    await SafeAreaManager.initialize();
    SafeAreaManager.watchFilamentTheme();
})();

/**
 * =============================================================================
 * VOICE RECORDER (Native + Web)
 * =============================================================================
 */

const VoiceRecorderModule = (() => {
    let mediaRecorder = null;
    let audioChunks = [];
    let isRecording = false;
    let voiceRecorderPlugin = null;

    const getVoiceRecorderPlugin = () => {
        if (!voiceRecorderPlugin && CapacitorUtils.isNative()) {
            voiceRecorderPlugin = CapacitorVoiceRecorder;
        }
        return voiceRecorderPlugin;
    };

    const getSupportedMimeType = () => {
        if (!window.MediaRecorder) return null;

        const mimeTypes = {
            'audio/webm': 'webm',
            'audio/ogg': 'ogg',
            'audio/mp4': 'mp4',
            'audio/mpeg': 'mp3',
        };

        for (const [mimeType, extension] of Object.entries(mimeTypes)) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
                return { mimeType, extension };
            }
        }

        return null;
    };

    const base64ToFile = (base64Data, mimeType) => {
        const base64Content = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;

        const byteCharacters = atob(base64Content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        let extension = 'aac';
        if (mimeType.includes('mp4')) extension = 'mp4';
        else if (mimeType.includes('webm')) extension = 'webm';
        else if (mimeType.includes('wav')) extension = 'wav';
        else if (mimeType.includes('ogg')) extension = 'ogg';
        else if (mimeType.includes('mpeg') || mimeType.includes('mp3')) extension = 'mp3';

        return new File([blob], `voice-message.${extension}`, {
            type: mimeType,
            lastModified: Date.now(),
        });
    };

    const VoiceRecorder = {
        async canRecord() {
            if (CapacitorUtils.isNative()) {
                try {
                    const plugin = getVoiceRecorderPlugin();
                    if (!plugin) return false;
                    const result = await plugin.canDeviceVoiceRecord();
                    return result.value === true;
                } catch (error) {
                    console.error('Error checking recording capability:', error);
                    return false;
                }
            } else {
                return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && getSupportedMimeType());
            }
        },

        async hasPermission() {
            if (CapacitorUtils.isNative()) {
                try {
                    const plugin = getVoiceRecorderPlugin();
                    if (!plugin) return false;
                    const result = await plugin.hasAudioRecordingPermission();
                    return result.value === true;
                } catch (error) {
                    console.error('Error checking permission:', error);
                    return false;
                }
            } else {
                return true; // permission prompt occurs on getUserMedia
            }
        },

        async requestPermission() {
            if (CapacitorUtils.isNative()) {
                try {
                    const plugin = getVoiceRecorderPlugin();
                    if (!plugin) return false;
                    const result = await plugin.requestAudioRecordingPermission();
                    return result.value === true;
                } catch (error) {
                    console.error('Error requesting permission:', error);
                    return false;
                }
            } else {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    stream.getTracks().forEach(t => t.stop());
                    return true;
                } catch (error) {
                    console.error('Error requesting microphone permission:', error);
                    return false;
                }
            }
        },

        async startRecording() {
            if (isRecording) {
                throw new Error('Already recording');
            }

            if (CapacitorUtils.isNative()) {
                const plugin = getVoiceRecorderPlugin();
                if (!plugin) throw new Error('Voice recorder plugin not available');
                await plugin.startRecording();
                isRecording = true;
            } else {
                const supportedType = getSupportedMimeType();
                if (!supportedType) throw new Error('No supported audio format found');

                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaRecorder = new MediaRecorder(stream, {
                    mimeType: supportedType.mimeType,
                    audioBitsPerSecond: 128000,
                });

                audioChunks = [];
                mediaRecorder.ondataavailable = e => {
                    if (e.data.size > 0) audioChunks.push(e.data);
                };

                mediaRecorder.start(1000); // 1-second chunks
                isRecording = true;
            }
        },

        async stopRecording() {
            if (!isRecording) return null;

            try {
                if (CapacitorUtils.isNative()) {
                    const plugin = getVoiceRecorderPlugin();
                    if (!plugin) return null;
                    const result = await plugin.stopRecording();

                    if (result.value && result.value.recordDataBase64) {
                        const mimeType = result.value.mimeType || 'audio/aac';
                        const base64Data = result.value.recordDataBase64.includes('data:')
                            ? result.value.recordDataBase64
                            : `data:${mimeType};base64,${result.value.recordDataBase64}`;
                        return base64ToFile(base64Data, mimeType);
                    }
                    return null;
                } else {
                    return new Promise(resolve => {
                        const supportedType = getSupportedMimeType();

                        mediaRecorder.onstop = () => {
                            const audioBlob = new Blob(audioChunks, { type: supportedType.mimeType });
                            const file = new File([audioBlob], `voice-message.${supportedType.extension}`, {
                                type: supportedType.mimeType,
                                lastModified: Date.now(),
                            });

                            // Clean up
                            mediaRecorder.stream.getTracks().forEach(t => t.stop());
                            mediaRecorder = null;
                            audioChunks = [];

                            resolve(file);
                        };

                        mediaRecorder.stop();
                    });
                }
            } finally {
                isRecording = false;
            }
        },

        async cancelRecording() {
            if (!isRecording) return;

            try {
                if (CapacitorUtils.isNative()) {
                    const plugin = getVoiceRecorderPlugin();
                    if (plugin) {
                        // Many plugins finalize on stop; we immediately drop any produced data.
                        await plugin.stopRecording();
                    }
                } else if (mediaRecorder && mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                    mediaRecorder.stream.getTracks().forEach(t => t.stop());
                    mediaRecorder = null;
                    audioChunks = [];
                }
            } catch (error) {
                console.error('Error canceling recording:', error);
            } finally {
                isRecording = false;
            }
        },

        get isRecording() {
            return isRecording;
        },

        getActiveStream() {
            // web only: the native plugin owns its own capture pipeline
            return mediaRecorder?.stream ?? null;
        },

        getSupportedMimeType,
    };

    return VoiceRecorder;
})();

/**
 * =============================================================================
 * PUSH NOTIFICATIONS (Native + Web)
 * =============================================================================
 */

const PushNotificationModule = (() => {
    let webManager = null;
    let isInitialized = false;
    let messageCallback = null;

    const getMobilePlugin = () => {
        if (CapacitorUtils.isNative()) {
            return CapacitorPushNotifications;
        }
        return null;
    };

    const getWebManager = async () => {
        if (!webManager && !CapacitorUtils.isNative()) {
            webManager = new PushNotificationManager();
            await webManager.init();
        }
        return webManager;
    };

    const PushNotifications = {
        async isSupported() {
            if (CapacitorUtils.isNative()) {
                return !!getMobilePlugin();
            } else {
                return 'serviceWorker' in navigator && 'Notification' in window;
            }
        },

        async hasPermission() {
            if (CapacitorUtils.isNative()) {
                try {
                    const plugin = getMobilePlugin();
                    if (!plugin) return false;
                    const result = await plugin.checkPermissions();
                    return result.receive === 'granted';
                } catch (error) {
                    console.error('Error checking permission:', error);
                    return false;
                }
            } else {
                return Notification.permission === 'granted';
            }
        },

        async requestPermission() {
            if (CapacitorUtils.isNative()) {
                try {
                    const plugin = getMobilePlugin();
                    if (!plugin) return false;

                    const permStatus = await plugin.requestPermissions();

                    if (permStatus.receive === 'granted') {
                        await plugin.register();

                        if (!isInitialized) {
                            await this._setupMobileListeners();
                            isInitialized = true;
                        }

                        if (CapacitorUtils.isAndroid()) {
                            await this._createAndroidChannels();
                        }

                        return true;
                    }
                    return false;
                } catch (error) {
                    console.error('Error requesting permission:', error);
                    return false;
                }
            } else {
                const manager = await getWebManager();
                return manager ? await manager.requestPermission() : false;
            }
        },

        async subscribe() {
            if (CapacitorUtils.isNative()) {
                return this.hasPermission();
            } else {
                const manager = await getWebManager();
                if (manager) {
                    return (await manager.getTokenAndSubscribe()) === true;
                }
                return false;
            }
        },

        async unsubscribe() {
            if (CapacitorUtils.isNative()) {
                try {
                    const csrf = document.querySelector('meta[name="csrf-token"]');
                    await fetch('/api/push-subscriptions', {
                        method: 'DELETE',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': csrf ? csrf.getAttribute('content') : '',
                            Accept: 'application/json',
                        },
                        body: JSON.stringify({
                            device_id: this._getDeviceId(),
                            platform: CapacitorUtils.getPlatform().toLowerCase(),
                        }),
                    });
                    return true;
                } catch (e) {
                    console.warn('Mobile unsubscribe failed', e);
                    return false;
                }
            } else {
                const manager = await getWebManager();
                if (manager) {
                    await manager.unsubscribe();
                    return true;
                }
                return false;
            }
        },

        async getStatus() {
            if (CapacitorUtils.isNative()) {
                const hasPermission = await this.hasPermission();
                if (!hasPermission) return 'permission-denied';
                return this._getDeviceId() ? 'subscribed' : 'not-subscribed';
            } else {
                const manager = await getWebManager();
                return manager ? await manager.checkSubscriptionStatus() : 'unsupported';
            }
        },

        async onMessage(callback) {
            messageCallback = callback;

            if (CapacitorUtils.isNative()) {
                // Handled via plugin listener below
            } else {
                const manager = await getWebManager();
                if (manager) manager.onMessage(callback);
            }
        },

        async checkAndPrompt() {
            if (CapacitorUtils.isNative()) {
                const hasPermission = await this.hasPermission();
                if (!hasPermission) {
                    window.dispatchEvent(
                        new CustomEvent('push-subscription-needed', { detail: { status: 'not-subscribed' } })
                    );
                    return false;
                }
                return true;
            } else {
                const manager = await getWebManager();
                return manager ? await manager.checkAndPromptForResubscription() : false;
            }
        },

        async init() {
            if (isInitialized) {
                console.log('[Push Notifications] Already initialized');
                return true;
            }

            try {
                if (CapacitorUtils.isNative()) {
                    const plugin = getMobilePlugin();
                    if (!plugin) {
                        console.warn('Push notifications plugin not available');
                        return false;
                    }

                    isInitialized = true;

                    const hasPermission = await this.hasPermission();
                    if (hasPermission) {
                        await this._setupMobileListeners();
                        // register on every launch, not only right after the permission
                        // prompt: iOS hands over the APNs token only once asked, and until it
                        // does, the native bridge drops Firebase's token refresh, so a rotated
                        // FCM token would never reach the server. The server upserts by token.
                        await plugin.register();
                        if (CapacitorUtils.isAndroid()) {
                            await this._createAndroidChannels();
                        }
                    }
                    return true;
                } else {
                    isInitialized = true;

                    const manager = await getWebManager();
                    if (manager) {
                        manager.startPeriodicCheck();
                        return true;
                    }
                    return false;
                }
            } catch (error) {
                console.error('Failed to initialize push notifications:', error);
                isInitialized = false;
                return false;
            }
        },

        _getDeviceId() {
            let deviceId = localStorage.getItem('device_push_id');
            if (!deviceId && CapacitorUtils.isNative()) {
                deviceId = `mobile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                localStorage.setItem('device_push_id', deviceId);
            }
            return deviceId;
        },

        async _setupMobileListeners() {
            const plugin = getMobilePlugin();
            if (!plugin) return;

            if (this._listenersSetup) {
                console.log('[Push Notifications] Listeners already setup');
                return;
            }
            this._listenersSetup = true;

            plugin.addListener('registration', async token => {
                // Avoid logging tokens in production
                if (import.meta.env.DEV) console.log('[Push Notifications] Registered for push');
                await this._sendTokenToServer(token.value);
            });

            plugin.addListener('registrationError', err => {
                console.error('Error on push notification registration:', err.error);
                if (typeof Sentry !== 'undefined' && Sentry.captureException) {
                    Sentry.captureException(new Error(err.error), {
                        tags: { component: 'push_notifications', context: 'registration_error' },
                        level: 'warning',
                    });
                }
            });

            plugin.addListener('pushNotificationReceived', notification => {
                if (messageCallback) {
                    messageCallback({
                        notification: {
                            title: notification.title,
                            body: notification.body,
                            ...notification,
                        },
                    });
                }

                window.dispatchEvent(new CustomEvent('push-notification-received', { detail: notification }));
            });

            plugin.addListener('pushNotificationActionPerformed', notification => {
                const data = notification.notification?.data || {};
                if (data.url && isSafeHttpUrl(data.url)) {
                    window.location.assign(data.url);
                }

                window.dispatchEvent(new CustomEvent('push-notification-tapped', { detail: notification }));
            });
        },
        _listenersSetup: false,

        async _sendTokenToServer(token) {
            try {
                const csrf = document.querySelector('meta[name="csrf-token"]');
                const response = await fetch('/api/push-subscriptions', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrf ? csrf.getAttribute('content') : '',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({
                        platform: CapacitorUtils.getPlatform().toLowerCase(),
                        token: token,
                        device_id: this._getDeviceId(),
                        device_model: navigator.userAgent,
                        app_version: window.APP_VERSION || '1.0.0',
                    }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json().catch(() => ({}));
                if (import.meta.env.DEV) console.log('[Push Notifications] Token registered with server:', data);
            } catch (error) {
                console.error('[Push Notifications] Error registering token with server:', error);
                if (typeof Sentry !== 'undefined' && Sentry.captureException) {
                    Sentry.captureException(error, {
                        tags: { component: 'push_notifications', context: 'token_registration' },
                        level: 'warning',
                    });
                }
            }
        },

        async _createAndroidChannels() {
            const plugin = getMobilePlugin();
            if (!plugin) return;

            if (this._channelsCreated) return;

            try {
                if (typeof plugin.createChannel !== 'function') {
                    console.log('createChannel not available on this platform/version');
                    return;
                }

                await plugin.createChannel({
                    id: 'fcm_default_channel',
                    name: 'General Notifications',
                    description: 'General app notifications',
                    importance: 5,
                    visibility: 1,
                    sound: 'default',
                    vibration: true,
                    lights: true,
                });

                await plugin.createChannel({
                    id: 'high_priority_channel',
                    name: 'Important Notifications',
                    description: 'High priority notifications',
                    importance: 5,
                    visibility: 1,
                    sound: 'default',
                    vibration: true,
                    lights: true,
                });

                this._channelsCreated = true;
            } catch (error) {
                console.error('Error creating notification channels:', error);
                if (typeof Sentry !== 'undefined' && Sentry.captureException) {
                    Sentry.captureException(error, {
                        tags: { component: 'push_notifications', context: 'android_channels' },
                        level: 'warning',
                    });
                }
            }
        },
        _channelsCreated: false,
    };

    return PushNotifications;
})();

window.Capacitor = Capacitor;
window.VoiceRecorder = VoiceRecorderModule;
window.PushNotifications = PushNotificationModule;
